import { useState, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import { useDeviceInventory } from "@/hooks/use-device-inventory";
import { useNeedsAttentionRollup, rollupToMap } from "@/hooks/use-needs-attention-rollup";
import { useDeviceInventoryColumns } from "@/components/device-inventory/device-inventory-columns";
import {
  DeviceInventoryFilters,
  type DeviceInventoryFilterValues,
  type RetiredFilter,
} from "@/components/device-inventory/device-inventory-filters";
import {
  PageHeader,
  Button,
  DataTable,
  Pagination,
  EmptyState,
  SkeletonTable,
} from "@/components/ui";

type DeviceInventory = components["schemas"]["DeviceInventory"];

export const Route = createFileRoute("/_authenticated/temporary-devices/")({
  component: DeviceInventoryListPage,
});

const PAGE_SIZE = 20;

function mapRetiredFilter(filter: RetiredFilter | undefined): boolean | undefined {
  if (filter === "retired") return true;
  if (filter === "active") return false;
  return undefined;
}

function DeviceInventoryListPage() {
  const { t } = useTranslation(["device-inventory", "org"]);
  const orgId = useAppStore((s) => s.activeOrgId);
  const { can } = usePermissions();
  const canCreate = can("device_inventory", "create");

  const navigate = useNavigate();
  const [filters, setFilters] = useState<DeviceInventoryFilterValues>({});
  const [offset, setOffset] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([{ id: "first_seen_at", desc: true }]);

  const firstSort = sorting[0];
  const sortBy = firstSort?.id as
    | "serial_number"
    | "first_seen_at"
    | "retired_at"
    | "created_at"
    | "updated_at"
    | "verdict"
    | undefined;
  const sortDir = firstSort ? (firstSort.desc ? "desc" : "asc") : undefined;

  useEffect(() => {
    setOffset(0);
  }, [sorting]);

  const { data, isLoading, isError } = useDeviceInventory({
    manufacturer_id: filters.manufacturer_id,
    retired_only: mapRetiredFilter(filters.retired_filter),
    sort_by: sortBy,
    sort_dir: sortDir,
    limit: PAGE_SIZE,
    offset,
  });

  // Fetch the inventory rollup ONCE for the visible page (no N+1). The flag
  // drills into the inventory detail page, where the installation chain
  // surfaces the affected active-non-compliant installation.
  const inventoryIds = useMemo(() => (data?.items ?? []).map((i) => i.id), [data]);
  const { data: rollupData } = useNeedsAttentionRollup("inventory", inventoryIds);
  const rollupMap = useMemo(() => rollupToMap(rollupData), [rollupData]);

  const columns = useDeviceInventoryColumns({
    rollup: rollupMap,
    onDrill: (inventoryId) =>
      void navigate({ to: "/temporary-devices/$id", params: { id: inventoryId } }),
  });

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleRowClick = (row: DeviceInventory) => {
    void navigate({ to: "/temporary-devices/$id", params: { id: row.id } });
  };

  return (
    <div className="p-8">
      <PageHeader
        title={t("device-inventory:list.title")}
        subtitle={t("device-inventory:list.subtitle")}
        actions={
          canCreate ? (
            <Button onClick={() => void navigate({ to: "/temporary-devices/new" })}>
              {t("device-inventory:list.createCta")}
            </Button>
          ) : undefined
        }
      />

      <div className="mt-6">
        <DeviceInventoryFilters
          filters={filters}
          onChange={(f) => {
            setFilters(f);
            setOffset(0);
          }}
        />
      </div>

      {isError && (
        <div className="mt-6 rounded-md bg-danger/10 p-4 text-danger">
          {t("device-inventory:errors.loadFailed")}
        </div>
      )}

      {isLoading ? (
        <div className="mt-6">
          <SkeletonTable columns={8} rows={5} />
        </div>
      ) : items.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title={t("device-inventory:list.empty")}
            actionLabel={canCreate ? t("device-inventory:list.emptyAction") : undefined}
            onAction={canCreate ? () => void navigate({ to: "/temporary-devices/new" }) : undefined}
          />
        </div>
      ) : (
        <>
          <div className="mt-6">
            <DataTable
              columns={columns}
              data={items}
              onRowClick={handleRowClick}
              sorting={sorting}
              onSortingChange={setSorting}
            />
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}
